/**
 * Client-side audio chunker using the Web Audio API (OfflineAudioContext).
 *
 * Splits large audio files into WAV blobs of at most `maxMB` each.
 * If the file is already within the limit, returns `[file]` unchanged.
 *
 * Browser-safe — never import from Deno/server code.
 *
 * @link docs/architecture/audio-chunking.md
 */

const BYTES_PER_SAMPLE = 2; // 16-bit PCM
const NUM_CHANNELS = 1; // mono — minimises output size
const WAV_HEADER_BYTES = 44;

/**
 * Encode a Float32Array of mono audio samples at the given sampleRate
 * into a 16-bit PCM WAV Blob.
 */
function encodePcmWav(samples: Float32Array, sampleRate: number): Blob {
  const numSamples = samples.length;
  const dataBytes = numSamples * BYTES_PER_SAMPLE;
  const buffer = new ArrayBuffer(WAV_HEADER_BYTES + dataBytes);
  const view = new DataView(buffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, NUM_CHANNELS, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * NUM_CHANNELS * BYTES_PER_SAMPLE, true); // byte rate
  view.setUint16(32, NUM_CHANNELS * BYTES_PER_SAMPLE, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeStr(36, "data");
  view.setUint32(40, dataBytes, true);

  let offset = WAV_HEADER_BYTES;
  for (let i = 0; i < numSamples; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return new Blob([buffer], { type: "audio/wav" });
}

/**
 * Split an audio File into an array of WAV File chunks, each at most
 * `maxMB` megabytes.  If the original file fits within `maxMB`, it is
 * returned as-is in a single-element array (no re-encode).
 */
export async function chunkAudioFile(file: File, maxMB = 24): Promise<File[]> {
  const maxBytes = maxMB * 1024 * 1024;

  if (file.size <= maxBytes) {
    return [file];
  }

  // Decode raw PCM via Web Audio API
  const arrayBuffer = await file.arrayBuffer();
  const audioCtx = new AudioContext();
  let decoded: AudioBuffer;
  try {
    decoded = await audioCtx.decodeAudioData(arrayBuffer);
  } finally {
    void audioCtx.close();
  }

  const sampleRate = decoded.sampleRate;
  const totalSamples = decoded.length;

  // Merge to mono (average channels)
  const mono = new Float32Array(totalSamples);
  for (let ch = 0; ch < decoded.numberOfChannels; ch++) {
    const channelData = decoded.getChannelData(ch);
    for (let i = 0; i < totalSamples; i++) {
      mono[i] += channelData[i];
    }
  }
  if (decoded.numberOfChannels > 1) {
    for (let i = 0; i < totalSamples; i++) {
      mono[i] /= decoded.numberOfChannels;
    }
  }

  // How many samples fit in maxBytes (minus WAV header)?
  const samplesPerChunk = Math.floor((maxBytes - WAV_HEADER_BYTES) / BYTES_PER_SAMPLE);

  const chunks: File[] = [];
  let offset = 0;
  let chunkIndex = 0;

  while (offset < totalSamples) {
    const end = Math.min(offset + samplesPerChunk, totalSamples);
    const slice = mono.slice(offset, end);
    const blob = encodePcmWav(slice, sampleRate);
    const baseName = file.name.replace(/\.[^.]+$/, "");
    const chunkFile = new File([blob], `${baseName}_chunk${chunkIndex}.wav`, {
      type: "audio/wav",
    });
    chunks.push(chunkFile);
    offset = end;
    chunkIndex++;
  }

  return chunks;
}
